import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Megaphone, BarChart3, Target, Plus, Save, Trash2, Edit3, Copy, X, Link2, TrendingUp,
  DollarSign, Eye, MousePointerClick, Zap, Search, Play, Pause, CheckCircle, Archive
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PLATFORM_META = {
  google: { label: 'Google Ads', color: 'bg-blue-500' },
  meta: { label: 'Meta / Instagram', color: 'bg-pink-500' },
  linkedin: { label: 'LinkedIn', color: 'bg-sky-600' },
  tiktok: { label: 'TikTok', color: 'bg-gray-800' },
  youtube: { label: 'YouTube', color: 'bg-red-600' },
  twitter: { label: 'X / Twitter', color: 'bg-slate-900' },
  snapchat: { label: 'Snapchat', color: 'bg-yellow-400' },
  whatsapp: { label: 'WhatsApp', color: 'bg-emerald-500' },
  email: { label: 'Email', color: 'bg-amber-500' },
  sms: { label: 'SMS', color: 'bg-indigo-500' },
  referral: { label: 'Referral', color: 'bg-purple-600' },
  seo: { label: 'SEO / Organic', color: 'bg-teal-600' },
  influencer: { label: 'Influencer', color: 'bg-rose-500' },
  other: { label: 'Other', color: 'bg-gray-500' },
};

const STATUS_META = {
  draft: { cls: 'bg-gray-100 text-gray-700' },
  scheduled: { cls: 'bg-sky-50 text-sky-700' },
  active: { cls: 'bg-emerald-50 text-emerald-700' },
  paused: { cls: 'bg-amber-50 text-amber-700' },
  completed: { cls: 'bg-indigo-50 text-indigo-700' },
  archived: { cls: 'bg-slate-100 text-slate-500' },
};

const CAMPAIGN_TYPES = ['lead_gen', 'awareness', 'conversion', 'retargeting', 'email_nurture', 'brand', 'traffic', 'engagement'];
const GEO_OPTIONS = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'RAK', 'Fujairah', 'UAQ', 'GCC', 'International', 'UK', 'EU', 'India', 'Pakistan', 'KSA'];

const emptyCampaign = () => ({
  name: '', platform: 'google', campaign_type: 'lead_gen', objective: '', status: 'draft',
  budget_total: '', budget_daily: '', spent: '', currency: 'AED',
  start_date: '', end_date: '', target_audience: '',
  geo_targets: [], age_min: '', age_max: '', interests: [], languages: ['EN'],
  creative_url: '', landing_url: '',
  utm: { source: '', medium: '', campaign: '', content: '', term: '' },
  kpis: { impressions: 0, clicks: 0, leads: 0, conversions: 0, revenue: 0 },
  tags: [], notes: '',
});

// ─── Utility: build UTM preview ───
const buildUtm = (landing, utm) => {
  if (!landing) return '';
  const params = Object.entries(utm || {}).filter(([, v]) => v).map(([k, v]) => `utm_${k}=${encodeURIComponent(v)}`);
  if (!params.length) return landing;
  return `${landing}${landing.includes('?') ? '&' : '?'}${params.join('&')}`;
};

const StatCard = ({ icon: Icon, label, value, sub, accent = 'bg-[#0B2F44]', 'data-testid': tid }) => (
  <div data-testid={tid} className="bg-white rounded-xl p-4 border border-[#D0D8DC]/50">
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg ${accent} text-white flex items-center justify-center`}><Icon className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-[#4A6572] uppercase tracking-wide font-semibold">{label}</p>
        <p className="text-lg font-bold text-[#0B2F44] mt-0.5 truncate">{value}</p>
        {sub && <p className="text-[10px] text-[#4A6572] mt-0.5">{sub}</p>}
      </div>
    </div>
  </div>
);

export default function CampaignManager({ isAr = false }) {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | campaign object
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [savingMsg, setSavingMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, sR] = await Promise.all([
        axios.get(`${API}/admin/campaigns`, { withCredentials: true }),
        axios.get(`${API}/admin/campaigns/stats`, { withCredentials: true }),
      ]);
      setCampaigns(cR.data);
      setStats(sR.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return campaigns.filter(c =>
      (filterPlatform === 'all' || c.platform === filterPlatform) &&
      (filterStatus === 'all' || c.status === filterStatus) &&
      (!q || (c.name || '').toLowerCase().includes(q) || (c.target_audience || '').toLowerCase().includes(q) || (c.objective || '').toLowerCase().includes(q))
    );
  }, [campaigns, filterPlatform, filterStatus, query]);

  const quickAction = async (c, status) => {
    try {
      await axios.put(`${API}/admin/campaigns/${c.id}`, { status }, { withCredentials: true });
      await load();
    } catch {}
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${c.id}`, { withCredentials: true });
      await load();
    } catch {}
  };

  const duplicateCampaign = async (c) => {
    try {
      await axios.post(`${API}/admin/campaigns/${c.id}/duplicate`, {}, { withCredentials: true });
      await load();
    } catch {}
  };

  const saveCampaign = async (payload) => {
    setSavingMsg('');
    const clean = { ...payload };
    ['budget_total', 'budget_daily', 'spent', 'age_min', 'age_max'].forEach(k => {
      clean[k] = clean[k] === '' || clean[k] == null ? null : Number(clean[k]);
    });
    if (clean.kpis) {
      const k = clean.kpis;
      clean.kpis = {
        impressions: Number(k.impressions || 0), clicks: Number(k.clicks || 0),
        leads: Number(k.leads || 0), conversions: Number(k.conversions || 0), revenue: Number(k.revenue || 0),
      };
    }
    try {
      if (clean.id) {
        const { id, ...rest } = clean;
        await axios.put(`${API}/admin/campaigns/${id}`, rest, { withCredentials: true });
        setSavingMsg(isAr ? 'تم التحديث ✓' : 'Saved ✓');
      } else {
        await axios.post(`${API}/admin/campaigns`, clean, { withCredentials: true });
        setSavingMsg(isAr ? 'تم الإنشاء ✓' : 'Created ✓');
      }
      await load();
      setTimeout(() => { setEditing(null); setSavingMsg(''); }, 800);
    } catch (e) {
      setSavingMsg(isAr ? `خطأ: ${e?.response?.data?.detail || e.message}` : `Error: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const t = stats?.totals || {};

  return (
    <div className="space-y-5" data-testid="campaign-manager">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard data-testid="campaign-stat-total" icon={Megaphone} label={isAr ? 'إجمالي الحملات' : 'Total Campaigns'} value={stats?.total ?? '—'}
          sub={stats ? `${stats.by_status?.active || 0} ${isAr ? 'نشطة' : 'active'} · ${stats.by_status?.draft || 0} ${isAr ? 'مسودة' : 'draft'}` : null} />
        <StatCard data-testid="campaign-stat-budget" icon={DollarSign} label={isAr ? 'الميزانية / المصروف' : 'Budget / Spent'} value={`${(t.budget || 0).toLocaleString()} AED`}
          sub={`${(t.spent || 0).toLocaleString()} ${isAr ? 'مصروف' : 'spent'} · ${(t.remaining || 0).toLocaleString()} ${isAr ? 'متبقي' : 'left'}`} accent="bg-emerald-600" />
        <StatCard data-testid="campaign-stat-leads" icon={Target} label={isAr ? 'العملاء المتوقعون' : 'Leads Generated'} value={(t.leads || 0).toLocaleString()}
          sub={`CPL: ${(t.avg_cpl || 0).toLocaleString()} AED · CTR: ${t.avg_ctr || 0}%`} accent="bg-[#C9A84C]" />
        <StatCard data-testid="campaign-stat-roi" icon={TrendingUp} label={'ROI'} value={`${t.roi ?? 0}%`}
          sub={`${(t.impressions || 0).toLocaleString()} ${isAr ? 'مشاهدة' : 'impressions'}`} accent={((t.roi || 0) >= 0) ? 'bg-emerald-600' : 'bg-rose-500'} />
      </div>

      {/* Filters + New button */}
      <div className="bg-white rounded-xl p-3 border border-[#D0D8DC]/50 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 text-[#4A6572] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="campaign-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder={isAr ? 'بحث...' : 'Search campaigns…'} className="h-10 ps-9 border-[#D0D8DC] text-sm" />
        </div>
        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="h-10 w-[160px] border-[#D0D8DC] text-xs" data-testid="campaign-filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{isAr ? 'كل المنصات' : 'All Platforms'}</SelectItem>
            {Object.entries(PLATFORM_META).map(([k, p]) => <SelectItem key={k} value={k} className="text-xs">{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-10 w-[150px] border-[#D0D8DC] text-xs" data-testid="campaign-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">{isAr ? 'كل الحالات' : 'All Statuses'}</SelectItem>
            {Object.keys(STATUS_META).map(k => <SelectItem key={k} value={k} className="text-xs capitalize">{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="campaign-new" onClick={() => setEditing(emptyCampaign())} className="bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm h-10">
          <Plus className="w-4 h-4 me-1" />{isAr ? 'حملة جديدة' : 'New Campaign'}
        </Button>
      </div>

      {/* Campaign cards */}
      <div className="space-y-3">
        {loading && <div className="text-center py-8 text-xs text-[#4A6572]">{isAr ? 'جاري التحميل...' : 'Loading…'}</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl p-10 border border-dashed border-[#D0D8DC] text-center text-sm text-[#4A6572]">
            {isAr ? 'لا توجد حملات مطابقة. اضغط "حملة جديدة" للبدء.' : 'No campaigns match. Click "New Campaign" to start.'}
          </div>
        )}
        {filtered.map(c => {
          const pm = PLATFORM_META[c.platform] || PLATFORM_META.other;
          const sm = STATUS_META[c.status] || STATUS_META.draft;
          const m = c.metrics || {};
          const spent = Number(c.spent || 0);
          const budget = Number(c.budget_total || 0);
          const pctSpent = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
          return (
            <div key={c.id} data-testid={`campaign-card-${c.id}`} className="bg-white rounded-xl border border-[#D0D8DC]/50 overflow-hidden">
              <div className="p-4">
                <div className="flex flex-wrap items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg ${pm.color} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>{c.platform?.slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-[#0B2F44]">{c.name}</p>
                      <Badge className={`${sm.cls} border-0 text-[10px] capitalize`} data-testid={`campaign-status-${c.id}`}>{c.status}</Badge>
                      <Badge className="bg-[#A68966]/10 text-[#A68966] border-0 text-[10px] capitalize">{(c.campaign_type || '').replace('_', ' ')}</Badge>
                      {c.languages?.map(l => <Badge key={l} className="bg-slate-50 text-slate-600 border-0 text-[10px]">{l}</Badge>)}
                    </div>
                    {c.objective && <p className="text-[11px] text-[#4A6572] mt-1">{c.objective}</p>}
                    <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-[#4A6572]">
                      {c.start_date && <span>📅 {c.start_date}{c.end_date ? ` → ${c.end_date}` : ''}</span>}
                      {c.geo_targets?.length > 0 && <span>🌍 {c.geo_targets.join(', ')}</span>}
                      {(c.age_min || c.age_max) && <span>👥 {c.age_min || 18}–{c.age_max || 65}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ms-auto">
                    {c.status === 'active' && (
                      <button onClick={() => quickAction(c, 'paused')} title="Pause" className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center" data-testid={`campaign-pause-${c.id}`}><Pause className="w-3.5 h-3.5" /></button>
                    )}
                    {(c.status === 'paused' || c.status === 'draft' || c.status === 'scheduled') && (
                      <button onClick={() => quickAction(c, 'active')} title="Activate" className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center justify-center" data-testid={`campaign-activate-${c.id}`}><Play className="w-3.5 h-3.5" /></button>
                    )}
                    {c.status !== 'completed' && c.status !== 'archived' && (
                      <button onClick={() => quickAction(c, 'completed')} title="Complete" className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 flex items-center justify-center" data-testid={`campaign-complete-${c.id}`}><CheckCircle className="w-3.5 h-3.5" /></button>
                    )}
                    {c.status !== 'archived' && (
                      <button onClick={() => quickAction(c, 'archived')} title="Archive" className="w-8 h-8 rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 flex items-center justify-center" data-testid={`campaign-archive-${c.id}`}><Archive className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => duplicateCampaign(c)} title="Duplicate" className="w-8 h-8 rounded-lg bg-sky-50 text-sky-700 hover:bg-sky-100 flex items-center justify-center" data-testid={`campaign-duplicate-${c.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditing({ ...c })} title="Edit" className="w-8 h-8 rounded-lg bg-[#0B2F44]/5 text-[#0B2F44] hover:bg-[#0B2F44]/10 flex items-center justify-center" data-testid={`campaign-edit-${c.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteCampaign(c)} title="Delete" className="w-8 h-8 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 flex items-center justify-center" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-4 pt-4 border-t border-[#D0D8DC]/30">
                  <MetricTile label={isAr ? 'الميزانية' : 'Budget'} value={`${(c.budget_total || 0).toLocaleString()} ${c.currency || 'AED'}`} sub={c.budget_daily ? `${c.budget_daily}/day` : null} />
                  <MetricTile label={isAr ? 'المصروف' : 'Spent'} value={`${(c.spent || 0).toLocaleString()}`} sub={budget > 0 ? `${pctSpent.toFixed(0)}%` : null} accent="text-rose-600" />
                  <MetricTile label={'CPL'} value={m.cpl != null ? `${m.cpl}` : '—'} sub={'AED/lead'} />
                  <MetricTile label={'CTR'} value={m.ctr != null ? `${m.ctr}%` : '—'} sub={`${(c.kpis?.clicks || 0).toLocaleString()} clicks`} />
                  <MetricTile label={isAr ? 'عملاء' : 'Leads'} value={(c.kpis?.leads || 0).toLocaleString()} sub={`${(c.kpis?.impressions || 0).toLocaleString()} imp`} accent="text-[#0B2F44]" />
                  <MetricTile label={'CVR / ROI'} value={`${m.cvr || 0}%`} sub={`ROI ${m.roi || 0}%`} />
                </div>

                {budget > 0 && (
                  <div className="mt-3 h-1.5 bg-[#F1F4F6] rounded-full overflow-hidden">
                    <div className={`h-full ${pctSpent > 90 ? 'bg-rose-500' : pctSpent > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pctSpent}%` }} />
                  </div>
                )}

                {c.utm_url && (
                  <div className="mt-3 flex items-center gap-2 bg-[#F8FAFB] rounded-lg px-3 py-2 text-[11px] text-[#4A6572]">
                    <Link2 className="w-3.5 h-3.5 text-[#A68966] flex-shrink-0" />
                    <span className="truncate flex-1 font-mono" title={c.utm_url}>{c.utm_url}</span>
                    <button onClick={() => navigator.clipboard?.writeText(c.utm_url)} className="text-[#0B2F44] hover:text-[#A68966] text-[10px] font-semibold" data-testid={`campaign-copy-url-${c.id}`}>{isAr ? 'نسخ' : 'Copy'}</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Funnel + Platform Breakdown */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4 border border-[#D0D8DC]/50">
            <h3 className="text-xs font-semibold text-[#0B2F44] uppercase tracking-wide mb-3 flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-[#A68966]" />{isAr ? 'قمع الحملات النشطة (أعلى 5)' : 'Active Funnel (Top 5)'}</h3>
            {(stats.funnel || []).length === 0 && <p className="text-xs text-[#4A6572]">{isAr ? 'لا توجد حملات نشطة' : 'No active campaigns'}</p>}
            <div className="space-y-3">
              {(stats.funnel || []).map(f => (
                <div key={f.id} className="text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-[#0B2F44] truncate me-2">{f.name}</span>
                    <span className="text-[#4A6572] text-[10px]">{PLATFORM_META[f.platform]?.label || f.platform}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    <FunnelStep icon={Eye} label="Impressions" value={f.impressions} />
                    <FunnelStep icon={MousePointerClick} label="Clicks" value={f.clicks} />
                    <FunnelStep icon={Target} label="Leads" value={f.leads} />
                    <FunnelStep icon={CheckCircle} label="Conv." value={f.conversions} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#D0D8DC]/50">
            <h3 className="text-xs font-semibold text-[#0B2F44] uppercase tracking-wide mb-3 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-[#A68966]" />{isAr ? 'توزيع حسب المنصة' : 'Platform Breakdown'}</h3>
            <div className="space-y-2">
              {Object.entries(stats.by_platform || {}).sort((a, b) => (b[1].spent || 0) - (a[1].spent || 0)).slice(0, 8).map(([k, v]) => {
                const pm = PLATFORM_META[k] || PLATFORM_META.other;
                const total = stats.totals.budget || 1;
                const pct = ((v.budget || 0) / total) * 100;
                return (
                  <div key={k} className="text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${pm.color}`} />
                        <span className="font-medium text-[#0B2F44]">{pm.label}</span>
                      </div>
                      <span className="text-[10px] text-[#4A6572]">
                        {v.count} · {(v.budget || 0).toLocaleString()} AED · {v.leads || 0} leads
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#F1F4F6] rounded-full overflow-hidden">
                      <div className={`h-full ${pm.color}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* UTM Builder Standalone */}
      <UTMBuilder isAr={isAr} />

      {/* Editor Modal */}
      {editing && <CampaignEditor campaign={editing} onClose={() => { setEditing(null); setSavingMsg(''); }} onSave={saveCampaign} isAr={isAr} savingMsg={savingMsg} />}
    </div>
  );
}

const MetricTile = ({ label, value, sub, accent = 'text-[#0B2F44]' }) => (
  <div>
    <p className="text-[9px] text-[#4A6572] uppercase tracking-wide font-semibold">{label}</p>
    <p className={`text-sm font-bold ${accent} mt-0.5`}>{value}</p>
    {sub && <p className="text-[10px] text-[#4A6572]">{sub}</p>}
  </div>
);

const FunnelStep = ({ icon: Icon, label, value }) => (
  <div className="bg-[#F8FAFB] rounded-lg py-1.5 px-1">
    <Icon className="w-3 h-3 mx-auto text-[#A68966]" />
    <p className="text-[10px] font-bold text-[#0B2F44] mt-0.5">{value.toLocaleString()}</p>
    <p className="text-[8px] text-[#4A6572] uppercase">{label}</p>
  </div>
);

// ─── UTM Builder ───
function UTMBuilder({ isAr }) {
  const [form, setForm] = useState({ url: 'https://gmre.ae/contact', source: '', medium: '', campaign: '', content: '', term: '' });
  const preview = useMemo(() => {
    const { url, ...utm } = form;
    return buildUtm(url, { source: utm.source, medium: utm.medium, campaign: utm.campaign, content: utm.content, term: utm.term });
  }, [form]);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(preview);
      } else {
        const ta = document.createElement('textarea');
        ta.value = preview; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch {}
        document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-[#D0D8DC]/50" data-testid="utm-builder">
      <h3 className="text-xs font-semibold text-[#0B2F44] uppercase tracking-wide mb-3 flex items-center gap-2"><Link2 className="w-3.5 h-3.5 text-[#A68966]" />{isAr ? 'أداة بناء روابط UTM' : 'UTM Link Builder'}</h3>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-3">
          <label className="text-[10px] text-[#4A6572] font-medium block mb-1">{isAr ? 'رابط الوجهة' : 'Landing URL'}</label>
          <Input data-testid="utm-url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://gmre.ae/page" className="h-9 border-[#D0D8DC] text-xs" />
        </div>
        {['source', 'medium', 'campaign', 'content', 'term'].map(k => (
          <div key={k}>
            <label className="text-[10px] text-[#4A6572] font-medium block mb-1">utm_{k}{(['source','medium','campaign']).includes(k) ? ' *' : ''}</label>
            <Input data-testid={`utm-${k}`} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={k === 'source' ? 'google, meta…' : k === 'medium' ? 'cpc, social, email…' : k === 'campaign' ? 'spring_sale' : ''} className="h-9 border-[#D0D8DC] text-xs" />
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 bg-[#F8FAFB] rounded-lg px-3 py-2 text-[11px] font-mono">
        <Link2 className="w-3.5 h-3.5 text-[#A68966] flex-shrink-0" />
        <span data-testid="utm-preview" className="truncate flex-1 text-[#0B2F44]" title={preview}>{preview}</span>
        <button data-testid="utm-copy" onClick={copy} className="text-[#0B2F44] hover:text-[#A68966] text-[10px] font-semibold">{copied ? (isAr ? 'تم النسخ ✓' : 'Copied ✓') : (isAr ? 'نسخ' : 'Copy')}</button>
      </div>
    </div>
  );
}

// ─── Campaign Editor Modal ───
function CampaignEditor({ campaign, onClose, onSave, isAr, savingMsg }) {
  const [form, setForm] = useState(campaign);
  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const updateUtm = (patch) => setForm(prev => ({ ...prev, utm: { ...prev.utm, ...patch } }));
  const updateKpi = (patch) => setForm(prev => ({ ...prev, kpis: { ...prev.kpis, ...patch } }));
  const toggleGeo = (g) => update({ geo_targets: form.geo_targets?.includes(g) ? form.geo_targets.filter(x => x !== g) : [...(form.geo_targets || []), g] });
  const toggleLang = (l) => update({ languages: form.languages?.includes(l) ? form.languages.filter(x => x !== l) : [...(form.languages || []), l] });

  const utmPreview = useMemo(() => buildUtm(form.landing_url, form.utm), [form.landing_url, form.utm]);

  return (
    <div className="fixed inset-0 z-50 bg-[#0B2F44]/70 flex items-start justify-center overflow-y-auto p-4" data-testid="campaign-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#D0D8DC]/50 px-5 py-3 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-[#0B2F44]">{form.id ? (isAr ? 'تعديل حملة' : 'Edit Campaign') : (isAr ? 'حملة جديدة' : 'New Campaign')}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#F8FAFB] flex items-center justify-center text-[#4A6572]" data-testid="campaign-editor-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5 max-h-[calc(100vh-180px)] overflow-y-auto">
          {/* Basics */}
          <Section title={isAr ? 'المعلومات الأساسية' : 'Campaign Basics'}>
            <Field label={isAr ? 'اسم الحملة *' : 'Campaign Name *'} span={2}>
              <Input data-testid="editor-name" value={form.name || ''} onChange={e => update({ name: e.target.value })} className="h-9 border-[#D0D8DC] text-sm" />
            </Field>
            <Field label="Platform *">
              <Select value={form.platform} onValueChange={v => update({ platform: v })}>
                <SelectTrigger className="h-9 border-[#D0D8DC] text-sm" data-testid="editor-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PLATFORM_META).map(([k, p]) => <SelectItem key={k} value={k} className="text-sm">{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={isAr ? 'نوع الحملة' : 'Campaign Type'}>
              <Select value={form.campaign_type} onValueChange={v => update({ campaign_type: v })}>
                <SelectTrigger className="h-9 border-[#D0D8DC] text-sm" data-testid="editor-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CAMPAIGN_TYPES.map(t => <SelectItem key={t} value={t} className="text-sm capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={v => update({ status: v })}>
                <SelectTrigger className="h-9 border-[#D0D8DC] text-sm" data-testid="editor-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_META).map(k => <SelectItem key={k} value={k} className="text-sm capitalize">{k}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={isAr ? 'الهدف' : 'Objective'} span={2}>
              <Input value={form.objective || ''} onChange={e => update({ objective: e.target.value })} placeholder={isAr ? 'ما الهدف من الحملة؟' : 'What is the goal of this campaign?'} className="h-9 border-[#D0D8DC] text-sm" />
            </Field>
          </Section>

          {/* Budget & Schedule */}
          <Section title={isAr ? 'الميزانية والجدولة' : 'Budget & Schedule'}>
            <Field label={isAr ? 'الميزانية الإجمالية' : 'Total Budget'}><Input type="number" value={form.budget_total ?? ''} onChange={e => update({ budget_total: e.target.value })} placeholder="5000" className="h-9 border-[#D0D8DC] text-sm" data-testid="editor-budget" /></Field>
            <Field label={isAr ? 'الميزانية اليومية' : 'Daily Budget'}><Input type="number" value={form.budget_daily ?? ''} onChange={e => update({ budget_daily: e.target.value })} placeholder="170" className="h-9 border-[#D0D8DC] text-sm" /></Field>
            <Field label={isAr ? 'المصروف' : 'Spent'}><Input type="number" value={form.spent ?? ''} onChange={e => update({ spent: e.target.value })} placeholder="0" className="h-9 border-[#D0D8DC] text-sm" /></Field>
            <Field label={isAr ? 'العملة' : 'Currency'}><Input value={form.currency || 'AED'} onChange={e => update({ currency: e.target.value })} className="h-9 border-[#D0D8DC] text-sm" /></Field>
            <Field label={isAr ? 'تاريخ البدء' : 'Start Date'}><Input type="date" value={form.start_date || ''} onChange={e => update({ start_date: e.target.value })} className="h-9 border-[#D0D8DC] text-sm" /></Field>
            <Field label={isAr ? 'تاريخ الانتهاء' : 'End Date'}><Input type="date" value={form.end_date || ''} onChange={e => update({ end_date: e.target.value })} className="h-9 border-[#D0D8DC] text-sm" /></Field>
          </Section>

          {/* Targeting */}
          <Section title={isAr ? 'الاستهداف' : 'Audience Targeting'}>
            <Field label={isAr ? 'وصف الجمهور' : 'Audience Description'} span={3}>
              <Textarea value={form.target_audience || ''} onChange={e => update({ target_audience: e.target.value })} rows={2} placeholder={isAr ? 'مثال: مقيمون في أبوظبي، 28-45 سنة، مهتمون بالعقارات' : 'e.g., Abu Dhabi residents, 28-45, interested in real estate'} className="border-[#D0D8DC] text-sm" />
            </Field>
            <Field label={isAr ? 'العمر الأدنى' : 'Age Min'}><Input type="number" value={form.age_min ?? ''} onChange={e => update({ age_min: e.target.value })} placeholder="25" className="h-9 border-[#D0D8DC] text-sm" /></Field>
            <Field label={isAr ? 'العمر الأعلى' : 'Age Max'}><Input type="number" value={form.age_max ?? ''} onChange={e => update({ age_max: e.target.value })} placeholder="55" className="h-9 border-[#D0D8DC] text-sm" /></Field>
            <Field label={isAr ? 'اللغات' : 'Languages'}>
              <div className="flex gap-1.5 flex-wrap pt-1">
                {['EN', 'AR', 'UR', 'HI', 'FR'].map(l => (
                  <button key={l} type="button" onClick={() => toggleLang(l)} data-testid={`editor-lang-${l}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border ${form.languages?.includes(l) ? 'bg-[#0B2F44] text-white border-[#0B2F44]' : 'bg-white text-[#4A6572] border-[#D0D8DC] hover:border-[#A68966]'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={isAr ? 'المناطق المستهدفة' : 'Geo Targets'} span={3}>
              <div className="flex gap-1.5 flex-wrap">
                {GEO_OPTIONS.map(g => (
                  <button key={g} type="button" onClick={() => toggleGeo(g)} data-testid={`editor-geo-${g.replace(/\s+/g, '-')}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border ${form.geo_targets?.includes(g) ? 'bg-[#A68966] text-white border-[#A68966]' : 'bg-white text-[#4A6572] border-[#D0D8DC] hover:border-[#A68966]'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={isAr ? 'اهتمامات (بفواصل)' : 'Interests (comma-sep)'} span={3}>
              <Input value={(form.interests || []).join(', ')} onChange={e => update({ interests: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} placeholder="real estate, mortgage, UAE property" className="h-9 border-[#D0D8DC] text-sm" />
            </Field>
          </Section>

          {/* Creative & UTM */}
          <Section title={isAr ? 'الإبداع والروابط' : 'Creative & Tracking'}>
            <Field label={isAr ? 'رابط الإبداع (صورة/فيديو)' : 'Creative URL (image/video)'} span={3}>
              <Input value={form.creative_url || ''} onChange={e => update({ creative_url: e.target.value })} placeholder="https://cdn.gmre.ae/creative.jpg" className="h-9 border-[#D0D8DC] text-sm" />
            </Field>
            <Field label={isAr ? 'رابط الوجهة' : 'Landing URL'} span={3}>
              <Input data-testid="editor-landing-url" value={form.landing_url || ''} onChange={e => update({ landing_url: e.target.value })} placeholder="https://gmre.ae/contact" className="h-9 border-[#D0D8DC] text-sm" />
            </Field>
            {['source', 'medium', 'campaign', 'content', 'term'].map(k => (
              <Field key={k} label={`utm_${k}`}>
                <Input data-testid={`editor-utm-${k}`} value={form.utm?.[k] || ''} onChange={e => updateUtm({ [k]: e.target.value })} className="h-9 border-[#D0D8DC] text-sm" />
              </Field>
            ))}
            {utmPreview && (
              <div className="lg:col-span-3 bg-[#F8FAFB] rounded-lg px-3 py-2 text-[11px] font-mono truncate" data-testid="editor-utm-preview" title={utmPreview}>
                <Link2 className="w-3 h-3 text-[#A68966] inline me-1.5" />{utmPreview}
              </div>
            )}
          </Section>

          {/* KPIs */}
          <Section title={isAr ? 'مؤشرات الأداء (KPIs)' : 'Performance KPIs'}>
            {['impressions', 'clicks', 'leads', 'conversions', 'revenue'].map(k => (
              <Field key={k} label={k}>
                <Input type="number" data-testid={`editor-kpi-${k}`} value={form.kpis?.[k] ?? 0} onChange={e => updateKpi({ [k]: e.target.value })} className="h-9 border-[#D0D8DC] text-sm" />
              </Field>
            ))}
          </Section>

          {/* Notes */}
          <Section title={isAr ? 'ملاحظات' : 'Notes & Tags'}>
            <Field label={isAr ? 'وسوم (بفواصل)' : 'Tags (comma-sep)'} span={3}>
              <Input value={(form.tags || []).join(', ')} onChange={e => update({ tags: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} placeholder="q4, high-priority, influencer" className="h-9 border-[#D0D8DC] text-sm" />
            </Field>
            <Field label={isAr ? 'ملاحظات' : 'Notes'} span={3}>
              <Textarea value={form.notes || ''} onChange={e => update({ notes: e.target.value })} rows={2} className="border-[#D0D8DC] text-sm" />
            </Field>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#D0D8DC]/50 px-5 py-3 flex items-center justify-between rounded-b-2xl">
          <p className="text-xs text-[#4A6572]" data-testid="editor-status-msg">{savingMsg}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="h-9 border-[#D0D8DC] text-sm">{isAr ? 'إلغاء' : 'Cancel'}</Button>
            <Button data-testid="editor-save" onClick={() => onSave(form)} disabled={!form.name || !form.platform} className="bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm h-9">
              <Save className="w-4 h-4 me-1.5" />{form.id ? (isAr ? 'حفظ التغييرات' : 'Save Changes') : (isAr ? 'إنشاء الحملة' : 'Create Campaign')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-bold text-[#0B2F44] uppercase tracking-wider mb-3 pb-2 border-b border-[#D0D8DC]/40">{title}</h4>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">{children}</div>
  </div>
);

const Field = ({ label, children, span = 1 }) => {
  const spanCls = span === 2 ? 'lg:col-span-2' : span === 3 ? 'lg:col-span-3' : '';
  return (
    <div className={spanCls}>
      <label className="text-[10px] text-[#4A6572] font-medium block mb-1">{label}</label>
      {children}
    </div>
  );
};
